/* Copyright (c) 2020-present CloudIO Inc */
import { ItemID, ThemeColor } from 'componentregistry';
import { useStateXGetter, useStateXValue } from '@cloudio-saas/statex';
import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { designPath, pageAtom } from 'designer/atoms';
import { getBGColor, getTextColor, readableColorFn } from 'theme/useThemeColor';

export type TabItemStyleProps = {
  color?: ThemeColor;
  flexDirection?: 'row' | 'column';
  wrapped: boolean;
  disableHoverEffect: boolean;
};

function getTabColor(theme: Theme, color?: ThemeColor) {
  return color === 'inherit'
    ? theme.palette.text.primary
    : color === 'default'
    ? theme.palette.background.default
    : getTextColor(theme, color);
}

const useTabStyles = makeStyles((theme: Theme) => ({
  root: ({
    wrapped,
    color,
    flexDirection,
    disableHoverEffect,
  }: TabItemStyleProps) => ({
    opacity: 1,
    '&:hover': {
      backgroundColor: disableHoverEffect
        ? 'unset'
        : theme.palette.action.hover,
    },
    minHeight: 24,
    // flex: 1,
    minWidth: 24,
    maxWidth: 253,
    marginRight: 0,
    color: theme.palette.text.secondary,
    flexDirection,
    justifyContent: 'flex-start',
    textTransform: 'initial' as const,
    '& .MuiTabItem-labelGroup': {
      minWidth: 0,
      flex: 1,
      maxWidth: '100%',
    },
    '& .MuiTabItem-label': {
      display: 'flex',
      alignItems: 'center',
      textAlign: 'left',
      whiteSpace: wrapped ? 'unset' : 'nowrap',
      '& .MuiTabItem-label-inner': {
        flex: 1,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
    },
    '& .MuiTabItem-subLabel': {
      fontSize: '0.75rem',
      textAlign: 'left',
      fontWeight: 'normal',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: wrapped ? 'unset' : 'nowrap',
    },
    '& .MuiTabItem-tag': {
      flexShrink: 0,
      backgroundColor: getTabColor(theme, color),
      color: readableColorFn(theme, getTabColor(theme, color)),
      fontSize: '0.75rem',
      borderRadius: 2,
      lineHeight: '16px',
      padding: '1px 4px',
      letterSpacing: 0.3,
      marginLeft: 8,
      whiteSpace: 'nowrap' as const,
    },
    '& svg': {
      fill: theme.palette.text.secondary,
      stroke: theme.palette.text.secondary,
    },
  }),
  ioIcon: ({ flexDirection }: TabItemStyleProps) => ({
    marginBottom: 0,
    flexShrink: 0,
    marginRight: flexDirection === 'column' ? 0 : 8,
  }),
  selected: ({ color }: TabItemStyleProps) => ({
    cursor: 'default',
    '& .MuiTabItem-labelGroup': {
      color: getTabColor(theme, color),
    },
    '& svg': {
      fill: getTabColor(theme, color),
      stroke: getTabColor(theme, color),
    },
  }),
}));

export type TabStyleProps = {
  color?: ThemeColor;
};

export const useTabsStyles = makeStyles((theme: Theme) => ({
  root: {
    boxShadow: 'inset 0 -1px 0 rgba(100,121,143,0.122)',
    minHeight: 'unset',
  },
  indicator: ({ color }: TabStyleProps) => ({
    height: '3px',
    backgroundColor:
      color === 'inherit' ? 'transparent' : getBGColor(theme, color),
    borderRadius: '2px',
  }),
}));

export default useTabStyles;

export const useIndicatorColor = (
  appUid: string,
  pageId: ItemID,
  parentId: ItemID,
  itemId: ItemID,
): ThemeColor => {
  const parentIndicatorColor = useStateXValue<ThemeColor | undefined>(
    designPath({
      appUid,
      pageId,
      itemId: parentId,
      key: 'props',
      subkey: 'indicatorColor',
    }),
    undefined,
  );
  const childIndicatorColor = useStateXValue<ThemeColor | undefined>(
    designPath({
      appUid,
      pageId,
      itemId,
      key: 'props',
      subkey: 'indicatorColor',
    }),
    undefined,
  );
  return childIndicatorColor || parentIndicatorColor || 'primary';
};

export const useParentIndicatorColor = (
  appUid: string,
  pageId: ItemID,
  itemId: ItemID,
) => {
  const get = useStateXGetter();
  const page = get(pageAtom, { params: { appUid, pageId } });
  const parentIndicatorColor = useStateXValue(
    designPath({
      appUid,
      pageId,
      itemId: page?.[itemId]?.parent,
      key: 'props',
      subkey: 'indicatorColor',
    }),
    undefined,
  );
  return parentIndicatorColor;
};

export const useParentProperty = (
  appUid: string,
  pageId: ItemID,
  itemId: ItemID,
  property: string,
) => {
  const parentItemId = useStateXValue(
    designPath({
      appUid,
      pageId,
      itemId,
      key: 'parent',
    }),
    undefined,
  );
  const parentProperty = useStateXValue(
    designPath({
      appUid,
      pageId,
      itemId: parentItemId,
      key: 'props',
      subkey: property,
    }),
    undefined,
  );
  return parentProperty;
};
